import { z } from "zod";
import {
  emptyStringAsUndefined,
  requiredHttpsUrlSchema,
  agentOptionalFragment,
  aiProviderFragment,
  authFragment,
  azureFragment,
  booleanEnv,
  clickhouseFragment,
  cosmoClickhouseFragment,
  cosmoClickhouseReaderFragment,
  cosmoClickhouseEraserFragment,
  cohereFragment,
  commaSeparatedListSchema,
  convexFragment,
  elevenlabsAgentFragment,
  elevenlabsPhoneImportFragment,
  langsmithFragment,
  logAndLLMFragment,
  metabaseFragment,
  nodeEnvSchema,
  pluralFetcherFragment,
  posthogFragment,
  posthogServerFragment,
  publicVercelEnvFragment,
  stripeFragment,
  turbopufferFragment,
  twilioFragment,
  vercelEnvFragment,
  wasteCalendarFragment,
} from "./fragments";

const pluralApiUrlSchema = z
  .string()
  .url()
  .refine(
    (url) => new URL(url).protocol === "https:",
    "PLURAL_API_URL must use https"
  );

// Blank or whitespace-only means "not configured": normalize to undefined so the
// Cosmo support bubble stays dormant instead of failing env parsing.
const cosmoSupportAgentTokenSchema = z
  .string()
  .trim()
  .transform((token): string | undefined =>
    token.length > 0 ? token : undefined
  );

export const agentsServerEnvShape = {
  NODE_ENV: nodeEnvSchema,
  NEXT_RUNTIME: z.enum(["nodejs", "edge"]).optional(),
  ...posthogServerFragment,
  ...authFragment,
  ...metabaseFragment,
  ...agentOptionalFragment,
  ...pluralFetcherFragment,
  ...clickhouseFragment,
  CLICKHOUSE_DATABASE: z
    .string()
    .regex(/^[A-Za-z_][A-Za-z0-9_]*$/)
    .optional(),
  PLURAL_API_URL: pluralApiUrlSchema.optional(),
  PLURAL_MASTER_ORG_ID: z.string().min(1).optional(),
  PLURAL_MASTER_EMAIL: z.string().email().optional(),
  PLURAL_MASTER_PASSWORD: z.string().min(1).optional(),
  ...vercelEnvFragment,
  ...elevenlabsPhoneImportFragment,
  TURBOPUFFER_API_KEY: turbopufferFragment.TURBOPUFFER_API_KEY,
  TURBOPUFFER_ENV: turbopufferFragment.TURBOPUFFER_ENV,
  AZURE_OPENAI_RESOURCE_NAME: azureFragment.AZURE_OPENAI_RESOURCE_NAME,
  AZURE_OPENAI_API_KEY: azureFragment.AZURE_OPENAI_API_KEY,
  // Post-call email routing. Carries the shared default ("gpt-4.1"),
  // so it needs no provisioning to work.
  AZURE_OPENAI_DEPLOYMENT: azureFragment.AZURE_OPENAI_DEPLOYMENT,
  CHAT_API_URL: z.string().url().optional(),
  CHAT_API_BYPASS_SECRET: z.string().min(1).optional(),
  CHAT_WIDGET_UPSTREAM_URL: z.string().url().optional(),
  CHAT_WIDGET_BYPASS_SECRET: z.string().min(1).optional(),
  CONVEX_URL: z.string().url().optional(),
  CONVEX_SITE_URL: z.string().min(1).optional(),
  CONVEX_PRODUCTION_URL: z.string().url().optional(),
  CONVEX_DEPLOYER_BYPASS_SECRET: z.string().min(1).optional(),
  VERCEL_URL: z.string().optional(),
  VOICEBOT_SERVICE_URL: z.string().url().optional(),
  VOICEBOT_PUBLIC_URL: z.string().url().optional(),
  VOICEBOT_SERVICE_BYPASS_SECRET: z.string().min(1),
  ELEVENLABS_ADMIN_KEY: z.string().min(1).optional(),
  // Workspace-level ElevenLabs post-call webhook id (per deployment). Referenced
  // by agent sync under platform_settings.workspace_overrides.webhooks so
  // completed calls POST to this deployment's /api/webhooks/post-call.
  ELEVENLABS_POST_CALL_WEBHOOK_ID: z.string().min(1).optional(),
  WIDGET_TTS_SIGNING_SECRET: z.string().min(1).optional(),
  CONVEX_WEBHOOK_SECRET: convexFragment.CONVEX_WEBHOOK_SECRET.optional(),
  ...cosmoClickhouseFragment,
  ...cosmoClickhouseReaderFragment,
  ...cosmoClickhouseEraserFragment,
  COSMO_CLICKHOUSE_READERS_ENABLED: booleanEnv.default(false),
};
export const agentsServerEnvSchema = z
  .object(agentsServerEnvShape)
  .superRefine((env, ctx) => {
    // Enforce all-or-none for Plural sysadmin fetcher credentials
    const pluralCreds = [
      env.PLURAL_SYSADMIN_EMAIL,
      env.PLURAL_SYSADMIN_PASSWORD,
      env.PLURAL_SYSADMIN_ORGANISATION_ID,
    ];
    const providedCount = pluralCreds.filter(Boolean).length;
    if (providedCount > 0 && providedCount < pluralCreds.length) {
      ctx.addIssue({
        code: "custom",
        message:
          "PLURAL_SYSADMIN_EMAIL, PLURAL_SYSADMIN_PASSWORD, and PLURAL_SYSADMIN_ORGANISATION_ID must be configured together.",
      });
    }

    const legacyPluralConfig = [
      env.PLURAL_API_URL,
      env.PLURAL_MASTER_ORG_ID,
      env.PLURAL_MASTER_EMAIL,
      env.PLURAL_MASTER_PASSWORD,
    ];
    const legacyProvidedCount = legacyPluralConfig.filter(Boolean).length;
    if (
      legacyProvidedCount > 0 &&
      legacyProvidedCount < legacyPluralConfig.length
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "PLURAL_API_URL, PLURAL_MASTER_ORG_ID, PLURAL_MASTER_EMAIL, and PLURAL_MASTER_PASSWORD must be configured together.",
      });
    }
  });

export const agentsClientEnvShape = {
  NEXT_PUBLIC_NODE_ENV: nodeEnvSchema,
  ...publicVercelEnvFragment,
  NEXT_PUBLIC_BETTER_AUTH_URL: z.string().min(1).optional(),
  NEXT_PUBLIC_CHATBUBBLE_URL: z.string().min(1),
  NEXT_PUBLIC_COSMO_TERMS_URL: requiredHttpsUrlSchema,
  NEXT_PUBLIC_COSMO_APP_PRIVACY_URL: requiredHttpsUrlSchema,
  NEXT_PUBLIC_COSMO_WEB_PRIVACY_URL: requiredHttpsUrlSchema,
  NEXT_PUBLIC_COSMO_SUPPORT_AGENT: cosmoSupportAgentTokenSchema.optional(),
  NEXT_PUBLIC_CONVEX_URL: z.string().url(),
  NEXT_PUBLIC_CONVEX_SITE_URL: z.string().min(1),
  NEXT_PUBLIC_SITE_URL: z.string().min(1).optional(),
  NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
  NEXT_PUBLIC_POSTHOG_HOST: z.string().optional(),
  NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
  NEXT_PUBLIC_APP_RELEASE: z.string().optional(),
  NEXT_PUBLIC_ADMIN_EMAILS: z.string().optional(),
  NEXT_PUBLIC_VERCEL_URL: z.string().optional(),
  NEXT_PUBLIC_WEBSITE_URL: z.string().url().optional(),
  NEXT_PUBLIC_DOCS_URL: z.string().url().optional(),
  // Stripe price IDs for frontend (monthly)
  NEXT_PUBLIC_STRIPE_PRICE_HOBBY: z.string().optional(),
  NEXT_PUBLIC_STRIPE_PRICE_STANDARD: z.string().optional(),
  NEXT_PUBLIC_STRIPE_PRICE_PRO: z.string().optional(),
  // Stripe price IDs for frontend (yearly)
  NEXT_PUBLIC_STRIPE_PRICE_HOBBY_YEARLY: z.string().optional(),
  NEXT_PUBLIC_STRIPE_PRICE_STANDARD_YEARLY: z.string().optional(),
  NEXT_PUBLIC_STRIPE_PRICE_PRO_YEARLY: z.string().optional(),
};

export const triggerEnvSchema = z.object({
  NEXT_PUBLIC_CONVEX_URL: z.string().url(),
  CONVEX_WEBHOOK_SECRET: convexFragment.CONVEX_WEBHOOK_SECRET,
  TURBOPUFFER_API_KEY: turbopufferFragment.TURBOPUFFER_API_KEY,
  TURBOPUFFER_ENV: turbopufferFragment.TURBOPUFFER_ENV,
  AZURE_OPENAI_RESOURCE_NAME: azureFragment.AZURE_OPENAI_RESOURCE_NAME,
  AZURE_OPENAI_API_KEY: azureFragment.AZURE_OPENAI_API_KEY,
  AZURE_OPENAI_EMBEDDING_DEPLOYMENT:
    azureFragment.AZURE_OPENAI_EMBEDDING_DEPLOYMENT,
  AZURE_OPENAI_EXTRACTION_DEPLOYMENT: z.string().min(1),
  // PostHog AI Observability for the content-ingestion pipeline.
  // OPTIONAL, and that is the load-bearing part — see the note below on why a
  // name added here becomes a hard boot dependency for every trigger task.
  //
  // ALL THREE go through emptyStringAsUndefined, which is not decoration: this
  // schema is parsed once, lazily, by EVERY trigger task that touches `env`, and
  // `vercel env pull` writes an unset variable as `NAME=`. A blank value has to
  // read as UNSET rather than as invalid, or an empty optional tracing variable
  // stops tasks that emit no telemetry at all from booting — the exact hard
  // boot dependency the note below warns about. (POSTHOG_HOST carries
  // this in posthogServerFragment; POSTHOG_KEY there does not, so it is wrapped
  // here rather than assumed.)
  POSTHOG_KEY: z.preprocess(emptyStringAsUndefined, z.string().optional()),
  POSTHOG_HOST: posthogServerFragment.POSTHOG_HOST,
  // The model name PostHog can PRICE, which is not the Azure DEPLOYMENT name
  // above: the deployment name is what the API call addresses, and PostHog has
  // no price for it, so $ai_total_cost_usd would sit at zero. Optional here so
  // telemetry never stops a task from booting. There is no code default: unset
  // records no cost. The pre-deploy check in
  // apps/agents/trigger/utils/deploy-env.ts requires it when POSTHOG_KEY is
  // configured.
  POSTHOG_EXTRACTION_PRICING_MODEL: z.preprocess(
    emptyStringAsUndefined,
    z.string().min(1).optional()
  ),
  // CRAWL4AI_BASE_URL / CRAWL4AI_API_TOKEN are deliberately NOT here, nor
  // anywhere else in the trigger surface. This schema is parsed once, lazily, by
  // every trigger task that touches env, so a name added here becomes a hard
  // boot dependency for ALL of them — a missing crawl var would take down
  // updateSource, which never crawls anything.
  //
  // They are not in trigger.config.ts's REQUIRED list either: the trigger tasks
  // render through the in-container worker, so the only consumer of the service
  // transport is the ship-gate harness at api/scrape/ingestion, which 404s
  // outside NODE_ENV=development. Both are laptop-only, read from process.env at
  // the point of use, and `renderPageViaService` throws CrawlRenderError when
  // either is absent.
});

export const chatApiEnvSchema = z
  .object({
    NODE_ENV: nodeEnvSchema,
    NEXT_RUNTIME: z.enum(["nodejs", "edge"]).optional(),
    NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
    VERCEL_GIT_COMMIT_SHA: z.string().optional(),
    ...posthogServerFragment,
    ...vercelEnvFragment,
    ...logAndLLMFragment,
    ...azureFragment,
    ...cohereFragment,
    ...aiProviderFragment,
    ...langsmithFragment,
    TURBOPUFFER_API_KEY: turbopufferFragment.TURBOPUFFER_API_KEY,
    TURBOPUFFER_REGION: turbopufferFragment.TURBOPUFFER_REGION,
    TURBOPUFFER_VECTOR_SEARCH_TOP_K:
      turbopufferFragment.TURBOPUFFER_VECTOR_SEARCH_TOP_K,
    TURBOPUFFER_BM25_SEARCH_TOP_K:
      turbopufferFragment.TURBOPUFFER_BM25_SEARCH_TOP_K,
    TURBOPUFFER_ENV: turbopufferFragment.TURBOPUFFER_ENV,
    // Knowledgebase Switchboard: default region for E.164 normalization of
    // local-format numbers extracted from the knowledge base. ISO 3166-1 alpha-2.
    SWITCHBOARD_DEFAULT_REGION: z
      .string()
      .regex(
        /^[A-Z]{2}$/,
        "SWITCHBOARD_DEFAULT_REGION must be a 2-letter uppercase ISO 3166-1 alpha-2 region code"
      )
      .default("DE"),
    NEXT_PUBLIC_CONVEX_URL: convexFragment.NEXT_PUBLIC_CONVEX_URL,
    CONVEX_DEPLOYER_BYPASS_SECRET: z.string().min(1).optional(),
    CONVEX_ADMIN_KEY: z.string().optional(),
    CONVEX_WEBHOOK_SECRET: convexFragment.CONVEX_WEBHOOK_SECRET,
    MASTRA_OBSERVABILITY_ENABLED: booleanEnv.default(true),
    MASTRA_MEMORY_ENABLED: booleanEnv.default(true),
    // Bounded evidence-driven investigation + answer verification.
    // Kill switch — set to false for single-pass answering.
    CHAT_INVESTIGATION_ENABLED: booleanEnv.default(true),
    CHATBOT_INPUT_CLASSIFIER_ENABLED: booleanEnv.default(true),
    CHATBOT_OUTPUT_CLASSIFIER_ENABLED: booleanEnv.default(true),
    TRANSLATE_ALLOWED_ORIGINS: z.string().optional(),
    TRUSTED_WIDGET_ORIGINS: commaSeparatedListSchema,
    CONVEX_URL: z.string().url().optional(),
    ...wasteCalendarFragment,
    // Billing config
    // Plural legacy token consumption API
    PLURAL_API_URL: pluralApiUrlSchema.optional(),
    PLURAL_MASTER_ORG_ID: z.string().min(1).optional(),
    PLURAL_MASTER_EMAIL: z.string().email().optional(),
    PLURAL_MASTER_PASSWORD: z.string().min(1).optional(),
    // ClickHouse for Plural credit validation
    ...clickhouseFragment,
    // Dedicated Cosmo history writer; never falls back to Plural credentials.
    ...cosmoClickhouseFragment,
  })
  .superRefine((env, ctx) => {
    // Enforce all-or-none for Plural API credentials
    const pluralConfig = [
      env.PLURAL_API_URL,
      env.PLURAL_MASTER_ORG_ID,
      env.PLURAL_MASTER_EMAIL,
      env.PLURAL_MASTER_PASSWORD,
    ];
    const providedCount = pluralConfig.filter(Boolean).length;
    if (providedCount > 0 && providedCount < pluralConfig.length) {
      ctx.addIssue({
        code: "custom",
        message:
          "PLURAL_API_URL, PLURAL_MASTER_ORG_ID, PLURAL_MASTER_EMAIL, and PLURAL_MASTER_PASSWORD must be configured together.",
      });
    }

    if (
      env.WASTE_CALENDAR_ENABLED &&
      (!env.WASTE_CALENDAR_SUPABASE_URL || !env.WASTE_CALENDAR_SUPABASE_API_KEY)
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "WASTE_CALENDAR_SUPABASE_URL and WASTE_CALENDAR_SUPABASE_API_KEY must be configured when WASTE_CALENDAR_ENABLED=true.",
      });
    }
  });

export const twilioEnvSchema = z.object({
  NODE_ENV: nodeEnvSchema,
  NEXT_RUNTIME: z.enum(["nodejs", "edge"]).optional(),
  NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
  ...posthogServerFragment,
  ...vercelEnvFragment,
  ...twilioFragment,
  NEXT_PUBLIC_CONVEX_URL: convexFragment.NEXT_PUBLIC_CONVEX_URL,
  CONVEX_SITE_URL: z.string().min(1),
  CONVEX_WEBHOOK_SECRET: convexFragment.CONVEX_WEBHOOK_SECRET,
  ...cosmoClickhouseFragment,
});

export const widgetToolingEnvSchema = z.object({
  VERCEL_AUTOMATION_BYPASS_SECRET: z.string().min(1).optional(),
  PLAYWRIGHT_BASE_URL: z.string().url().optional(),
  CHAT_WIDGET_E2E_NAVIGATION_DELAY_MS: z.preprocess((value) => {
    if (value === undefined) return undefined;
    const delay = Number(value);
    return Number.isFinite(delay) && delay > 0 ? delay : 0;
  }, z.number().optional()),
  CI: z.string().optional(),
});

export const widgetServerEnvSchema = z
  .object({
    NODE_ENV: nodeEnvSchema,
    NEXT_RUNTIME: z.enum(["nodejs", "edge"]).optional(),
    NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
    NEXT_PUBLIC_APP_RELEASE: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_HOST: z.string().optional(),
    ...posthogServerFragment,
    ...vercelEnvFragment,
    CHAT_API_URL: z.string().url(),
    CHAT_API_BYPASS_SECRET: z.string().min(1).optional(),
    NEXT_PUBLIC_CHAT_API_URL: z.string().url().optional(),
    NEXT_PUBLIC_COSMO_URL: requiredHttpsUrlSchema,
    NEXT_PUBLIC_COSMO_WEB_PRIVACY_URL: requiredHttpsUrlSchema,
    CHAT_API_MODEL_PROVIDER: z.string().optional(),
    CHAT_API_TIMEOUT_MS: z.coerce.number().int().positive().default(20000),
    TRUSTED_WIDGET_ORIGINS: commaSeparatedListSchema,
    DEFAULT_ENVIRONMENT: z.string().optional(),
    CONVEX_URL: z.string().url().optional(),
    CONVEX_WEBHOOK_SECRET: convexFragment.CONVEX_WEBHOOK_SECRET,
    NEXT_PUBLIC_AGENTS_URL: z.string().url().optional(),
    NEXT_PUBLIC_CONVEX_URL: z.string().url().optional(),
    ELEVENLABS_API_KEY: z.string().optional(),
    MICROSOFT_SPEECH_KEY: z.string().optional(),
    MICROSOFT_SPEECH_REGION: z.string().optional(),
    NEXT_PUBLIC_PLURAL_API_URL: z.string().optional(),
    LOG_LEVEL: logAndLLMFragment.LOG_LEVEL,
    RESEND_API_KEY: z.string().min(1).optional(),
    WIDGET_TTS_SIGNING_SECRET: z.string().min(1).optional(),
    ...widgetToolingEnvSchema.shape,
  })
  .superRefine((env, ctx) => {
    if (env.VERCEL_ENV === "preview" && !env.NEXT_PUBLIC_AGENTS_URL) {
      ctx.addIssue({
        code: "custom",
        path: ["NEXT_PUBLIC_AGENTS_URL"],
        message: "NEXT_PUBLIC_AGENTS_URL is required for preview deployments.",
      });
    }
  });

export const voicebotEnvSchema = z.object({
  NODE_ENV: nodeEnvSchema,
  ...posthogServerFragment,
  ...vercelEnvFragment,
  PORT: z.coerce.number().int().positive().optional(),
  ELEVENLABS_ADMIN_KEY: elevenlabsAgentFragment.ELEVENLABS_ADMIN_KEY,
  ELEVENLABS_WEBHOOK_SECRET: elevenlabsAgentFragment.ELEVENLABS_WEBHOOK_SECRET,
  NEXT_PUBLIC_CONVEX_URL: convexFragment.NEXT_PUBLIC_CONVEX_URL,
  CONVEX_SITE_URL: z.string().min(1),
  CONVEX_WEBHOOK_SECRET: convexFragment.CONVEX_WEBHOOK_SECRET,
  // RAG proxy
  CHAT_API_URL: z.string().url(),
  CHAT_API_BYPASS_SECRET: z.string().min(1).optional(),
  // Service
  VOICEBOT_SERVICE_URL: z.string().url().optional(),
  VOICEBOT_PUBLIC_URL: z.string().url().optional(),
  VOICEBOT_SERVICE_BYPASS_SECRET: z.string().min(1),
  // Post-call transcripts are pushed to the same LangSmith project as chat-api
  // so a voice call and its RAG lookups share one thread.
  ...langsmithFragment,
  ...cosmoClickhouseFragment,
});

export const convexEnvSchema = z.object({
  NODE_ENV: nodeEnvSchema,
  CONVEX_CLOUD_URL: z.string().url().optional(),
  BYPASS_SUPERADMIN: z.string().optional(),
  RESEND_API_KEY: z.string().min(1).optional(),
  // When "true", short-circuit every Resend send (CI kill-switch). See sendTemplateEmail.
  RESEND_DRY_RUN: z.string().optional(),
  RESEND_TMPL_AUTH_PASSWORD_RESET: z.string().min(1).optional(),
  RESEND_TMPL_AUTH_EMAIL_VERIFY: z.string().min(1).optional(),
  RESEND_TMPL_AUTH_ORG_INVITATION: z.string().min(1).optional(),
  RESEND_TMPL_AUTH_MAGIC_LINK: z.string().min(1).optional(),
  RESEND_TMPL_BILLING_USAGE_NOTIF: z.string().min(1).optional(),
  RESEND_TMPL_BILLING_TRIAL_EXPIRY: z.string().min(1).optional(),
  RESEND_TMPL_LEAD_CAPTURE: z.string().min(1).optional(),
  RESEND_TMPL_SIGNUP_NOTIFICATION: z.string().min(1).optional(),
  RESEND_TMPL_POST_CALL_SUMMARY: z.string().min(1).optional(),
  SITE_URL: z.string().url().optional(),
  BETTER_AUTH_URL: z.string().url().optional(),
  PLURAL_AUTH_URL: z.string().url().optional(),
  WIDGET_ALLOWED_ORIGINS: z.string().optional(),
  ADMIN_EMAILS: z.string().optional(),
  VOICEBOT_SERVICE_URL: z.string().url().optional(),
  VOICEBOT_SERVICE_BYPASS_SECRET: z.string().min(1).optional(),
  BETTER_AUTH_TRUSTED_ORIGINS: z.string().optional(),
  CONVEX_WEBHOOK_SECRET: z.string().min(1).optional(),
  CONVEX_DEPLOYER_BYPASS_SECRET: z.string().min(1).optional(),
  TRIGGER_SECRET_KEY: z.string().min(1).optional(),
  // Selects the Trigger preview branch for a preview deployment. The key names
  // an environment, not a branch; the branch rides the x-trigger-branch header.
  TRIGGER_PREVIEW_BRANCH: z.string().min(1).optional(),
  TEST_USER_SECRET: z.string().optional(),
  COSMO_LEGAL_VERSION: z.string().min(1).optional(),
  COSMO_ANONYMOUS_AUTH_ENABLED: booleanEnv.default(false),
  COSMO_DEPLOYMENT_ENVIRONMENT: z
    .enum(["development", "preview", "production"])
    .default("production"),
  // ClickHouse admin login for nightly retention blanking; the action fails when one is missing.
  COSMO_CLICKHOUSE_ADMIN_URL: z.preprocess(emptyStringAsUndefined, z.string().url().optional()),
  COSMO_CLICKHOUSE_ADMIN_USER: z.preprocess(emptyStringAsUndefined, z.string().min(1).optional()),
  COSMO_CLICKHOUSE_ADMIN_PASSWORD: z.preprocess(emptyStringAsUndefined, z.string().min(1).optional()),
  // The post-call outcome event captures to PostHog by direct fetch.
  // Both optional, blank reading as unset, exactly as the Trigger runtime
  // composes them above: a missing key disables the capture and nothing else.
  POSTHOG_KEY: z.preprocess(emptyStringAsUndefined, z.string().optional()),
  POSTHOG_HOST: posthogServerFragment.POSTHOG_HOST,
  AGENT_ENTITLEMENT_ENFORCEMENT_ENABLED: booleanEnv.default(false),
  // Stripe (for payments/subscriptions)
  STRIPE_SECRET_KEY: stripeFragment.STRIPE_SECRET_KEY.optional(),
  STRIPE_WEBHOOK_SECRET: stripeFragment.STRIPE_WEBHOOK_SECRET.optional(),
  // Stripe price IDs (monthly)
  STRIPE_PRICE_HOBBY: z.string().optional(),
  STRIPE_PRICE_STANDARD: z.string().optional(),
  STRIPE_PRICE_PRO: z.string().optional(),
  // Stripe price IDs (yearly)
  STRIPE_PRICE_HOBBY_YEARLY: z.string().optional(),
  STRIPE_PRICE_STANDARD_YEARLY: z.string().optional(),
  STRIPE_PRICE_PRO_YEARLY: z.string().optional(),
  // Stripe metered price IDs (shared between monthly/yearly)
  STRIPE_PRICE_HOBBY_METERED: z.string().optional(),
  STRIPE_PRICE_STANDARD_METERED: z.string().optional(),
  STRIPE_PRICE_PRO_METERED: z.string().optional(),
  // Vector deletion. Optional here, like every other feature-scoped
  // secret in this schema: getConvexEnv() is a single memoized parse shared by
  // every Convex function, so a required entry would take the whole deployment
  // down when unset rather than just vector cleanup. The fail-fast guarantee is
  // enforced at the use site instead — see requireTurbopufferConfig() in
  // convex/agents/vectorCleanup.ts, which throws rather than skipping deletion.
  TURBOPUFFER_API_KEY: turbopufferFragment.TURBOPUFFER_API_KEY.optional(),
  TURBOPUFFER_ENV: turbopufferFragment.TURBOPUFFER_ENV.optional(),
  // Purge switch: only "true"/"1" is on. No booleanEnv: a typo must not throw here.
  MASTRA_MEMORY_PURGE_ENABLED: z.string().optional().transform(
    (value) => ["true", "1"].includes(value?.trim().toLowerCase() ?? ""),
  ),
});

export const schemas = {
  agentsServerEnvShape,
  agentsServerEnvSchema,
  agentsClientEnvShape,
  triggerEnvSchema,
  chatApiEnvSchema,
  twilioEnvSchema,
  voicebotEnvSchema,
  widgetToolingEnvSchema,
  widgetServerEnvSchema,
  convexEnvSchema,
  fragments: {
    azureFragment,
    authFragment,
    elevenlabsAgentFragment,
    metabaseFragment,
    pluralFetcherFragment,
    stripeFragment,
    turbopufferFragment,
    twilioFragment,
    logAndLLMFragment,
    posthogFragment,
    posthogServerFragment,
    wasteCalendarFragment,
  },
};

export type TriggerEnv = z.infer<typeof triggerEnvSchema>;
export type ChatApiEnv = z.infer<typeof chatApiEnvSchema>;
export type TwilioEnv = z.infer<typeof twilioEnvSchema>;
export type VoicebotEnv = z.infer<typeof voicebotEnvSchema>;
export type WidgetToolingEnv = z.infer<typeof widgetToolingEnvSchema>;
export type WidgetServerEnv = z.infer<typeof widgetServerEnvSchema>;
export type ConvexEnv = z.infer<typeof convexEnvSchema>;
